/**
 * «چرا حقوقِ این نفر این‌قدر شد؟»
 *
 * چرا ساخته شد: کاربر پرسید «حقوق ثابتِ مصطفی ۵۳۸ میلیون است، چرا فیشش ۷۱
 * میلیون شده؟» و جوابش فقط با کوئریِ دستی در دیتابیس پیدا می‌شد. علتِ واقعی
 * این بود که برای او فقط **۲ روز** حضور ثبت شده بود و موتور بقیهٔ روزهای کاری
 * را غیبت حساب کرده بود. حالا با یک کلیک روی نامِ هر کس همین زنجیره دیده می‌شود.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card, Col, Descriptions, Drawer, Empty, Row, Space, Spin, Statistic, Table, Tag, Typography } from "antd";
import Alert from "./GxAlert";
import { formatMoney } from "../utils/money";
import { toJalali } from "../utils/dateUtils";
import { JALALI_MONTHS } from "../utils/dateUtils";
import { apiError } from "../utils/apiError";
import { explainPayroll } from "../api/client";

const { Text } = Typography;
const money = (v) => formatMoney(v);

const PAY_MODEL = { fixed: "حقوق ثابت", piece: "کارمزدی", daily: "حقوق ثابت", none: "بدون حقوق ثابت" };

/**
 * بدنهٔ تفکیک — بدونِ دراور، تا داخلِ «پروندهٔ حقوقی» هم جا شود.
 * `data` را می‌گیرد؛ اگر `periodId/employeeId` بدهی خودش می‌خواند.
 */
export function PayrollExplainBody({ data, compact = false }) {
  if (!data) return null;
  const emp = data.employee;
  const att = data.attendance;
  const rec = data.record;
  const ins = data.insurance;

  const chain = [];
  if (emp && rec) {
    if (emp.pay_model !== "piece" && emp.monthly_salary) {
      chain.push({ label: "حقوق ثابت ماهانه", amount: emp.monthly_salary });
      const cut = emp.monthly_salary - rec.gross_pay;
      if (cut > 0) {
        chain.push({
          label: `کسرِ غیبت و کسریِ کار (${att.missing_days} روزِ بدونِ ثبتِ حضور)`,
          amount: -cut,
        });
      }
    }
    chain.push({ label: "ناخالصِ حقوق", amount: rec.gross_pay, strong: true });
    if (rec.insurance_deduction) {
      chain.push({ label: "کسرِ بیمه (طبقِ لیستِ بیمه)", amount: -rec.insurance_deduction });
    }
    const L = { advance_deduction: "مساعده", buffet_deduction: "بوفه",
      manual_deduction: "کسورات دستی" };
    Object.keys(L).forEach((f) => {
      if (rec[f]) chain.push({ label: L[f], amount: -rec[f] });
    });
    chain.push({ label: "خالصِ پرداختی", amount: rec.net_pay, strong: true });
  }

  return (
    <>
      {(data.warnings || []).map((w, i) => (
        <Alert key={i} type="warning" showIcon style={{ marginBottom: 10 }} message={w} />
      ))}

      {att && (
        <Card size="small" title="حضور و غیابِ این ماه" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col xs={8}><Statistic title="روزِ کاری" value={att.working_days} /></Col>
            <Col xs={8}><Statistic title="حضورِ ثبت‌شده" value={att.present_days}
              valueStyle={{ color: att.present_days ? "#107C41" : "#C0392B" }} /></Col>
            <Col xs={8}><Statistic title="بدونِ ثبت" value={att.missing_days}
              valueStyle={{ color: att.missing_days ? "#C0392B" : undefined }} /></Col>
          </Row>
          {att.shortage_minutes > 0 && (
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
              کسریِ کار: {att.shortage_minutes} دقیقه
            </Text>
          )}
          {!compact && att.dates?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>روزهای ثبت‌شده: </Text>
              <Space size={4} wrap>{att.dates.map((d) => <Tag key={d}>{toJalali(d)}</Tag>)}</Space>
            </div>
          )}
        </Card>
      )}

      {rec ? (
        <Card size="small" title="زنجیرهٔ محاسبه" style={{ marginBottom: 12 }}>
          <Table size="small" pagination={false} rowKey={(_, i) => i} dataSource={chain}
            columns={[
              { title: "", dataIndex: "label", render: (v, r) => (r.strong ? <b>{v}</b> : v) },
              {
                title: "مبلغ (ریال)", dataIndex: "amount", align: "left", width: 170,
                render: (v, r) => (
                  <span style={{ color: v < 0 ? "#C0392B" : undefined,
                    fontWeight: r.strong ? 700 : 400 }}>{money(v)}</span>
                ),
              },
            ]} />
        </Card>
      ) : (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="برای این پرسنل در این دوره فیشی محاسبه نشده است." />
      )}

      {ins && (
        <Card size="small" title="ردیفِ لیستِ بیمه" style={{ marginBottom: 12 }}>
          <Descriptions size="small" bordered column={1}>
            <Descriptions.Item label="حق بیمه سهم کارمند">{money(ins.premium)}</Descriptions.Item>
            <Descriptions.Item label="خالص پرداختی">{money(ins.net_payable)}</Descriptions.Item>
            <Descriptions.Item label="جمعِ کسر از حقوق"><b>{money(ins.deduction)}</b></Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </>
  );
}

/** هوکِ خواندنِ تفکیک — برای جاهایی که خودشان دراور دارند (مثلِ پروندهٔ حقوقی). */
export function usePayrollExplain(periodId, employeeId) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!periodId || !employeeId) { setData(null); return; }
    explainPayroll(periodId, employeeId)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [periodId, employeeId]);
  return data;
}

export default function PayrollExplainDrawer({ periodId, employeeId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!periodId || !employeeId) return;
    setLoading(true);
    setErr(null);
    explainPayroll(periodId, employeeId)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e, "اطلاعاتِ این پرسنل خوانده نشد")))
      .finally(() => setLoading(false));
  }, [periodId, employeeId]);

  useEffect(() => { load(); }, [load]);

  const emp = data?.employee;
  const att = data?.attendance;
  const rec = data?.record;
  const ins = data?.insurance;

  /** زنجیرهٔ محاسبه — همان ترتیبی که موتور طی می‌کند. */
  const chain = [];
  if (emp && rec) {
    if (emp.pay_model !== "piece" && emp.monthly_salary) {
      chain.push({ k: "پایه", label: "حقوق ثابت ماهانه", amount: emp.monthly_salary });
      const cut = emp.monthly_salary - rec.gross_pay;
      if (cut > 0) {
        chain.push({
          k: "کسر",
          label: `کسرِ غیبت و کسریِ کار (${att.missing_days} روزِ بدونِ ثبتِ حضور)`,
          amount: -cut, warn: att.missing_days > 0,
        });
      }
    }
    chain.push({ k: "ناخالص", label: "ناخالصِ حقوق", amount: rec.gross_pay, strong: true });
    if (rec.insurance_deduction) {
      chain.push({ k: "کسر", label: "کسرِ بیمه (طبقِ لیستِ بیمه)", amount: -rec.insurance_deduction });
    }
    ["advance_deduction", "buffet_deduction", "manual_deduction"].forEach((f) => {
      const L = { advance_deduction: "مساعده", buffet_deduction: "بوفه", manual_deduction: "کسورات دستی" };
      if (rec[f]) chain.push({ k: "کسر", label: L[f], amount: -rec[f] });
    });
    chain.push({ k: "خالص", label: "خالصِ پرداختی", amount: rec.net_pay, strong: true });
  }

  return (
    <Drawer open={!!employeeId} onClose={onClose} width="min(720px, 96vw)"
      title={emp ? `${emp.name} — ${data.period ? `${JALALI_MONTHS[data.period.month - 1]} ${data.period.year}` : ""}` : "جزئیات"}>
      {loading && <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>}
      {err && <Alert type="error" showIcon message={err} />}
      {!loading && !err && !data && <Empty description="داده‌ای نیست" />}

      {data && (
        <>
          <Descriptions size="small" bordered column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="کد پرسنلی">{emp.code || "—"}</Descriptions.Item>
            <Descriptions.Item label="سمت">{emp.job_title || "—"}</Descriptions.Item>
            <Descriptions.Item label="نوع حقوق">{PAY_MODEL[emp.pay_model] || emp.pay_model}</Descriptions.Item>
            <Descriptions.Item label="حقوق ثابت">{money(emp.monthly_salary)}</Descriptions.Item>
            <Descriptions.Item label="حضور و غیاب">
              {emp.attendance_required ? <Tag color="blue">مشمول</Tag> : <Tag>غیرمشمول</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="بیمه">
              {emp.has_insurance ? <Tag color="cyan">دارد</Tag> : <Tag>ندارد</Tag>}
            </Descriptions.Item>
          </Descriptions>
          <PayrollExplainBody data={data} />
        </>
      )}
    </Drawer>
  );
}
